import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Container } from "components";
import Breadcrumb from "components/molecules/Breadcrumb";
import { CKEditor } from "ckeditor4-react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getData, putData } from "actions";
import { API_URL_companysetting, base } from "constants/api";
import { companySettingReducers } from "redux/companySettingSlice";
import { AuthContext } from "context/AuthContext";

const Company = () => {
  const logoImage = `${base}/media/logo/logo.png`;
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const { getCompanySettingResult, addCompanySettingResult } = useSelector(
    (state) => state.companySetting
  );

  const formik = useFormik({
    initialValues: {
      company_name: "",
      website: "",
      phone: "",
      logo: "",
      address: false,
      use_logo: "",
    },
    validationSchema: Yup.object({
      company_name: Yup.string().required("Name is required"),
      website: Yup.string().url().required("Website is required"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("company_name", formik.values.company_name);
      formData.append("website", formik.values.website);
      formData.append("phone", formik.values.phone);
      formData.append("address", formik.values.address);
      formData.append("use_logo", formik.values.use_logo);
      // Tambahkan pengecekan apakah file gambar ada sebelum ditambahkan ke FormData
      if (formik.values.logo !== "") {
        formData.append("logo", formik.values.logo);
      }
      //edit on id 1
      putData(
        `${API_URL_companysetting}1/`,
        formData,
        { dispatch, redux: companySettingReducers },
        "ADD_COMPANYSETTING",
        () => {
          fetchData();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
        }
      );
    },
  });

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_companysetting,
        params,
        { dispatch, redux: companySettingReducers },
        "GET_COMPANYSETTING"
      );
    },
    [dispatch]
  );

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  const fetchData = useCallback(() => {
    //get id 1 for setting
    const params = `1/`;
    get(params);
  }, [get]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addCompanySettingResult) {
      fetchData();
    }
  }, [addCompanySettingResult, fetchData]);

  useEffect(() => {
    if (getCompanySettingResult) {
      formik.setFieldValue(
        "company_name",
        getCompanySettingResult.company_name
      );
      formik.setFieldValue("website", getCompanySettingResult.website);
      formik.setFieldValue("phone", getCompanySettingResult.phone);
      formik.setFieldValue("use_logo", getCompanySettingResult.use_logo);
      formik.setFieldValue("address", getCompanySettingResult.address);
    }
  }, [getCompanySettingResult]);

  const toolbarConfig = {
    extraPlugins: "font,colorbutton,colordialog",
    removePlugins: "about,image",
    skin: "moono",
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Settings"}
        excludedPaths={["setting", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Company settings
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-3">
              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-1 lg:col-span-4 lg:col-start-1">
                <label className="sm:text-sm">
                  Name Company <span className="text-red-500 ml-1">*</span>
                </label>
                <div>
                  <input
                    type="text"
                    name="company_name"
                    id="company_name"
                    onChange={formik.handleChange}
                    ref={(ref) => (inputRefs.current[0] = ref)}
                    value={formik.values.company_name}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 sm:text-sm sm:leading-6"
                  />
                  {formik.touched.company_name &&
                    formik.errors.company_name && (
                      <div className="text-red-500 dark:text-red-300 text-xs">
                        {formik.errors.company_name}
                      </div>
                    )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-5 lg:col-span-4 lg:col-start-5">
                <label className="sm:text-sm">Website</label>
                <div>
                  <input
                    type="url"
                    name="website"
                    id="website"
                    onChange={formik.handleChange}
                    placeholder="https://domain.com"
                    value={formik.values.website}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 sm:text-sm sm:leading-6"
                  />
                  {formik.touched.website && formik.errors.website && (
                    <div className="text-red-500 dark:text-red-300 text-xs">
                      {formik.errors.website}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-span-full mb-1.5 md:col-span-4 md:col-start-9 lg:col-span-4 lg:col-start-9">
                <label className="sm:text-sm">Phone</label>
                <div>
                  <input
                    type="number"
                    name="phone"
                    id="phone"
                    onChange={formik.handleChange}
                    value={formik.values.phone}
                    className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-1">
              <label className="sm:text-sm">Address</label>
              <div>
                {formik.values.address !== false && (
                  <CKEditor
                    config={toolbarConfig}
                    type="classic"
                    initData={formik.values.address}
                    onChange={(e) => {
                      formik.setFieldValue("address", e.editor.getData());
                    }}
                  />
                )}
              </div>
            </div>

            <div className="mt-4 grid grid-cols-6 gap-4 mb-4 ">
              <div className="col-span-2 col-start-1">
                <label className="sm:text-sm mr-2">Logo</label>
                <input
                  type="file"
                  id="logo"
                  name="logo"
                  onChange={(e) => {
                    formik.setFieldValue("logo", e.target.files[0]);
                  }}
                  className="border border-gray-300 p-2 rounded-md w-3/4"
                />
              </div>
              <div className="col-span-1 col-start-3">
                <label>
                  <input
                    id="use_logo"
                    name="use_logo"
                    type="checkbox"
                    checked={formik.values.use_logo === 1}
                    onChange={(e) =>
                      formik.setFieldValue("use_logo", e.target.checked ? 1 : 0)
                    }
                    className="form-checkbox mr-2 mt-3"
                  />
                  Use logo
                </label>
              </div>
              <div className="col-span-1 col-start-4">
                <img src={logoImage} className="border-dotted" />
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </Container>
  );
};

export default Company;
